import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search,
  ArrowLeft,
  Building2,
  Package,
  Grid3X3,
  Users,
  MapPin,
  DollarSign,
  Plus
} from "lucide-react";
import { useServices } from "@/hooks/useServices";
import { useVendors } from "@/hooks/useVendors";
import { useServiceCategories } from "@/hooks/useServiceCategories";
import { useBooking } from "@/hooks/useBookings";
import type { ServiceResponse } from "@/types/services";
import type { Vendor } from "@/types/vendors";
import AddMarketplaceOptionModal from "./AddMarketplaceOptionModal";
import ServiceCategoryBrowser from "./ServiceCategoryBrowser";

interface BookingMarketplaceBrowserProps {
  bookingId: string;
}

export default function BookingMarketplaceBrowser({ bookingId }: BookingMarketplaceBrowserProps) {
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("categories");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddOptionModal, setShowAddOptionModal] = useState(false);
  const [selectedServiceOptionId, setSelectedServiceOptionId] = useState<string | null>(null);
  const [selectedServiceOptionData, setSelectedServiceOptionData] = useState<any>(null);

  // Data hooks
  const { data: booking } = useBooking(bookingId);
  const { data: services = [], loading: servicesLoading } = useServices();
  const { vendors = [], isLoading: vendorsLoading } = useVendors();
  const { data: categories = [], isLoading: categoriesLoading } = useServiceCategories(true);

  // Filter data based on search
  const filteredServices = useMemo(() => {
    if (!searchTerm) return services;
    return services.filter(service =>
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [services, searchTerm]);

  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendors;
    return vendors.filter(vendor =>
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [vendors, searchTerm]);

  const handleServiceSelect = (serviceId: string) => {
    setLocation(`/bookings/${bookingId}/marketplace/services/${serviceId}`);
  };

  const handleAddOption = (serviceOptionId: string, serviceOptionData: any) => {
    setSelectedServiceOptionId(serviceOptionId);
    setSelectedServiceOptionData(serviceOptionData);
    setShowAddOptionModal(true);
  };

  const handleBack = () => {
    setLocation(`/bookings/${bookingId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Booking
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Marketplace Browser</h1>
          <p className="text-muted-foreground">
            Finding alternatives for: {booking?.activityName} - {booking?.organizationName}
          </p>
        </div>
      </div>

      <Separator />

      {/* Browse Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Grid3X3 className="w-4 h-4" />
            By Category
          </TabsTrigger>
          <TabsTrigger value="vendors" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            By Vendor
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            All Services
          </TabsTrigger>
        </TabsList>

        {/* Browse by Category */}
        <TabsContent value="categories" className="space-y-4">
          <ServiceCategoryBrowser
            onServiceSelect={(service) => {
              // Create a booking option from the selected service
              handleAddOption(service.id, {
                serviceName: service.name,
                vendorName: service.vendorName,
                basePrice: service.basePrice,
                currency: service.currency,
                capacity: service.capacity,
                location: service.location,
                duration: service.duration,
                description: service.description,
                tags: service.tags
              });
            }}
            showPricing={true}
          />
        </TabsContent>

        {/* Browse by Vendor */}
        <TabsContent value="vendors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Browse by Vendor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="h-[400px]">
                {vendorsLoading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredVendors.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{vendor.name}</p>
                            <p className="text-sm text-muted-foreground">{vendor.contactEmail}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {vendor.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {filteredVendors.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No vendors found matching your search.
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Browse All Services */}
        <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Browse All Services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="h-[400px]">
                {servicesLoading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredServices.map((service) => (
                      <div
                        key={service.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/5 cursor-pointer"
                        onClick={() => handleServiceSelect(service.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{service.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {service.vendorName}
                            </p>
                            {service.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {service.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Category</p>
                            <p className="font-medium">{categories.find(c => c.id === service.serviceCategoryId)?.name || "Uncategorized"}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredServices.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No services found matching your search.
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Option Modal */}
      {selectedServiceOptionId && (
        <AddMarketplaceOptionModal
          open={showAddOptionModal}
          onClose={() => {
            setShowAddOptionModal(false);
            setSelectedServiceOptionId(null);
            setSelectedServiceOptionData(null);
          }}
          bookingId={bookingId}
          serviceOptionId={selectedServiceOptionId}
          serviceOptionData={selectedServiceOptionData}
          onSuccess={() => {
            setShowAddOptionModal(false);
            setSelectedServiceOptionId(null);
            setSelectedServiceOptionData(null);
            setLocation(`/bookings/${bookingId}`);
          }}
        />
      )}
    </div>
  );
}